import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Simulating FontAwesome icons with simple SVG components
const IconCloud = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z"/>
  </svg>
);

const IconFile = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M14 2H6C4.9 2 4 2.9 4 4v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm-1 7V3.5L18.5 9H13z"/>
  </svg>
);

const IconVideo = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
  </svg>
);

const IconProject = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
  </svg>
);

const IconList = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z"/>
  </svg>
);

const IconEmail = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
  </svg>
);

const IconToggle = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M17 7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h10c2.76 0 5-2.24 5-5s-2.24-5-5-5zm0 8c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z"/>
  </svg>
);

// Container component
const Container = ({ children, className, id }) => (
  <div className={`container mx-auto px-4 ${className || ''}`} id={id}>
    {children}
  </div>
);

const MS365AlternativeUniverse = () => {
  const { t } = useTranslation('home');
  const [hoveredBubble, setHoveredBubble] = useState(null);
  const [animationCompleted, setAnimationCompleted] = useState(false);
  const universeRef = useRef(null);
  const [rotation, setRotation] = useState(0);
  const [isUniverseHovered, setIsUniverseHovered] = useState(false);

  // Get the entire ms365Alternative object first
  const ms365Data = useMemo(() => t('ms365Alternative', { returnObjects: true }), [t]);
  
  // Handle hover for subtle rotation
  useEffect(() => {
    // Target rotation values
    const restPosition = 0;
    const hoverPosition = 15; // rotate 15 degrees when hovered
    
    if (isUniverseHovered) {
      // Smoothly rotate to hover position
      const rotationInterval = setInterval(() => {
        setRotation(prevRotation => {
          if (prevRotation >= hoverPosition) {
            clearInterval(rotationInterval);
            return hoverPosition;
          }
          return prevRotation + 0.5;
        });
      }, 50);
      
      return () => clearInterval(rotationInterval);
    } else {
      // Smoothly rotate back to rest position
      const rotationInterval = setInterval(() => {
        setRotation(prevRotation => {
          if (prevRotation <= restPosition) {
            clearInterval(rotationInterval);
            return restPosition;
          }
          return prevRotation - 0.5;
        });
      }, 50);
      
      return () => clearInterval(rotationInterval);
    }
  }, [isUniverseHovered]);

  // Feature data mapped from the ms365Alternative object
  const features = useMemo(() => {
    if (!ms365Data?.features?.list) return [];
    
    const icons = [
      <IconCloud />,    // index 0 - Datei-Management
      <IconFile />,     // index 1 - Office
      <IconVideo />,    // index 2 - Video
      <IconProject />,  // index 3 - Project
      <IconList />,     // index 4 - Wiki
      <IconEmail />     // index 5 - Email
    ];
    
    const positions = [30, 90, 150, 210, 270, 330];
    
    return ms365Data.features.list.map((feature, index) => ({
      id: feature.name.toLowerCase().replace(/\s+/g, '-'),
      title: feature.name,
      product: feature.tool,
      description: feature.description,
      icon: icons[index],
      iconBgColor: '#3b82f6',
      orbitRadius: 320,
      orbitPosition: positions[index],
      size: 200
    }));
  }, [ms365Data]);

  // Calculate positions based on orbit
  const calculatePosition = (feature) => {
    const angle = (feature.orbitPosition + rotation) * (Math.PI / 180);
    const x = Math.cos(angle) * feature.orbitRadius;
    const y = Math.sin(angle) * feature.orbitRadius;
    return { x, y };
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      // Optional: Add resize logic if needed
    };
    
    window.addEventListener('resize', handleResize);
    
    // Trigger initial animation
    const timer = setTimeout(() => {
      setAnimationCompleted(true);
    }, 1000);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, []);

  // CSS for animations
  const animationStyles = useMemo(() => `
    @media (min-width: 1024px) {
      #ms365-alternative-section {
        min-height: 800px;
      }
    }
    
    @keyframes fade-in-up {
      0% {
        opacity: 0;
        transform: translateY(10px);
      }
      100% {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `, []);

  // Add animation styles to document
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = animationStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, [animationStyles]);

  // Prevent rendering before data is loaded
  if (!ms365Data) return null;

  return (
    <Container className="py-16 relative overflow-hidden" id="ms365-alternative-section">
      {/* Section header with high contrast */}
      <div className="text-center mb-12">
        <h2 className="text-2xl font-bold text-primary mb-2">
          {ms365Data.features?.title}
        </h2>
        <div className="w-32 h-0.5 bg-primary mx-auto"></div>
      </div>
        
        <div className="max-w-6xl mx-auto relative">
          {/* Desktop view with interactive universe */}
          <div 
            ref={universeRef}
            className="hidden lg:block relative" 
            style={{ height: '800px' }}
            onMouseEnter={() => setIsUniverseHovered(true)}
            onMouseLeave={() => setIsUniverseHovered(false)}
          >
            {/* Orbit circle - more subtle and matching the design */}
            <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[640px] h-[640px] rounded-full border border-gray-700/30 opacity-30" />
            
            {/* Central bubble with pulsing animation */}
            <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 
                          w-[340px] h-[340px] rounded-full bg-gray-800/95 border border-blue-600/30 
                          flex flex-col items-center justify-between pt-8 pb-6 px-6 text-center z-30
                          shadow-lg shadow-blue-900/20">
              <div className="w-14 h-14 bg-blue-600 rounded-full flex items-center justify-center mb-3"
                   style={{ 
                     background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                     boxShadow: '0 0 15px 0 rgba(59,130,246,0.3)'
                   }}>
                <div className="text-white transform scale-125">
                  <IconToggle />
                </div>
              </div>
              
              <div>
                <h3 className="text-xl font-bold text-white mb-2">
                  {ms365Data.introduction?.title}
                </h3>
                
                <p className="text-gray-300 text-sm mb-4 leading-normal max-w-[270px]">
                  {ms365Data.introduction?.description}
                </p>
              </div>
              
              <button 
                className="group relative px-6 py-3 rounded-full text-white font-medium text-sm 
                         overflow-hidden transition-all duration-300 hover:shadow-lg 
                         hover:shadow-blue-500/25 flex items-center justify-center"
                style={{ minWidth: '240px' }}
                aria-label={ms365Data.cta}
                onClick={() => window.location.href = '/contact'}
              >
                {/* Gradient background with hover effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-500 
                              transition-transform duration-300 group-hover:scale-105" />
                
                {/* Button content */}
                <div className="relative flex items-center">
                  <span className="mr-2 text-lg transform transition-transform duration-300 
                                 group-hover:scale-110">
                    <IconEmail />
                  </span>
                  <span className="transition-transform duration-300 group-hover:translate-x-1">
                    {ms365Data.cta}
                  </span>
                </div>
                
                {/* Hover shine effect */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity 
                              duration-300 bg-gradient-to-r from-transparent via-white to-transparent 
                              transform translate-x-[-100%] group-hover:translate-x-[100%] 
                              transition-transform duration-700" />
              </button>
            </div>
            
            {/* Feature bubbles with calculated positions */}
            {features.map((feature, i) => {
              const position = calculatePosition(feature);
              const isHovered = hoveredBubble === feature.id;
              
              return (
                <div 
                  key={`feature-${i}`}
                  className={`absolute rounded-full bg-gray-800/95 
                           border ${isHovered ? 'border-blue-400' : 'border-blue-600/30'} 
                           flex flex-col items-center text-center 
                           cursor-pointer z-20 transition-all duration-300
                           hover:shadow-lg hover:shadow-blue-600/20
                           transform ${animationCompleted ? 'scale-100' : 'scale-0'}`}
                  style={{ 
                    left: `calc(50% + ${position.x}px)`,
                    top: `calc(50% + ${position.y}px)`,
                    width: `${feature.size}px`,
                    height: `${feature.size}px`,
                    transform: `translate(-50%, -50%) scale(${isHovered ? 1.1 : 1})`,
                    transition: `all 0.3s ease-out, left 0.5s ease-out, top 0.5s ease-out, transform 0.3s ease-out, border-color 0.3s ease-out, box-shadow 0.3s ease-out`,
                    opacity: animationCompleted ? 1 : 0,
                  }}
                  onMouseEnter={() => setHoveredBubble(feature.id)}
                  onMouseLeave={() => setHoveredBubble(null)}
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center mt-4 mb-2
                                 text-white transform transition-transform ${isHovered ? 'scale-110' : 'scale-100'}`}
                       style={{ backgroundColor: feature.iconBgColor }}>
                    {feature.icon}
                  </div>
                  
                  <h4 className="font-medium text-white text-sm px-3 mb-1 leading-tight">
                    {feature.title}
                  </h4>
                  
                  <div className="text-blue-400 text-xs font-medium mb-1">
                    {feature.product}
                  </div>
                  
                  <div className={`text-gray-400 text-xs px-3 leading-tight max-w-[150px] transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-80'}`}>
                    {feature.description}
                  </div>
                </div>
              );
            })}
            
            {/* Connecting lines with solar system orbital connections */}
            <svg className="absolute inset-0 w-full h-full z-10 pointer-events-none" viewBox="0 0 1000 800">
              <defs>
                <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#1d4ed8" stopOpacity="0.05" />
                  <stop offset="50%" stopColor="#3b82f6" stopOpacity="0.25" />
                  <stop offset="100%" stopColor="#1d4ed8" stopOpacity="0.05" />
                </linearGradient>
                
                <filter id="subtleGlow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="2" result="blur" />
                  <feComposite in="SourceGraphic" in2="blur" operator="over" />
                </filter>
              </defs>
              
            {/* Orbital ring connecting all outer bubbles - more subtle */}
            <circle 
              cx="500" 
              cy="400" 
              r="320" 
              fill="none" 
              stroke="#3b82f6" 
              strokeWidth="1" 
              strokeDasharray="4,4"
              opacity="0.4"
            />
              
              {/* Radial lines from center to each feature */}
              {features.map((feature, i) => {
                const position = calculatePosition(feature);
                const isHovered = hoveredBubble === feature.id;
                
                // Center coordinates (SVG viewBox coordinates)
                const centerX = 500;
                const centerY = 400;
                
                // Feature coordinates
                const featureX = centerX + position.x;
                const featureY = centerY + position.y;
                
                // Calculate vector from center to feature
                const vectorX = featureX - centerX;
                const vectorY = featureY - centerY;
                
                // Calculate distance
                const distance = Math.sqrt(vectorX * vectorX + vectorY * vectorY);
                const normalizedX = vectorX / distance;
                const normalizedY = vectorY / distance;
                
                // Inner and outer points
                const innerRadius = 170; // Center bubble radius
                const outerRadius = feature.size / 2;
                
                const innerX = centerX + normalizedX * innerRadius;
                const innerY = centerY + normalizedY * innerRadius;
                
                const outerX = featureX - normalizedX * outerRadius;
                const outerY = featureY - normalizedY * outerRadius;
                
                return (
                  <g key={`connection-${i}`}>
                    {/* Radial connection line */}
                    <line 
                      x1={innerX}
                      y1={innerY}
                      x2={outerX}
                      y2={outerY}
                      stroke={isHovered ? "#3b82f6" : "url(#lineGradient)"}
                      strokeWidth={isHovered ? 2 : 1}
                      strokeDasharray={isHovered ? "5,3" : "4,4"}
                      opacity={isHovered ? 1 : 0.4}
                      filter={isHovered ? "url(#subtleGlow)" : "none"}
                    >
                      {isHovered && (
                        <animate 
                          attributeName="stroke-dashoffset" 
                          values="0;-16" 
                          dur="1.5s"
                          repeatCount="indefinite"
                        />
                      )}
                    </line>
                  </g>
                );
              })}
            </svg>
          </div>
          
          {/* Mobile view - improved stacked cards */}
          <div className="lg:hidden space-y-6">
            {/* Main content */}
            <div className="rounded-xl bg-gray-800/95 border border-blue-600/30 p-6 text-center shadow-lg shadow-blue-900/20">
              <div className="w-14 h-14 mx-auto mb-4 bg-blue-600 rounded-full flex items-center justify-center animate-pulse-slow text-white">
                <IconToggle />
              </div>
              
              <h3 className="text-xl font-bold text-white mb-3">
                {ms365Data.introduction?.title}
              </h3>
              
              <p className="text-gray-300 text-sm mb-6">
                {ms365Data.introduction?.description}
              </p>
              
              <button 
                className="group relative px-6 py-3 rounded-full text-white font-medium text-sm 
                         overflow-hidden transition-all duration-300 hover:shadow-lg 
                         hover:shadow-blue-500/25 flex items-center justify-center w-full"
                aria-label={ms365Data.cta}
                onClick={() => window.location.href = '/contact'}
              >
                {/* Gradient background with hover effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-500 
                              transition-transform duration-300 group-hover:scale-105" />
                
                {/* Button content */}
                <div className="relative flex items-center">
                  <span className="mr-2 text-lg transform transition-transform duration-300 
                                 group-hover:scale-110">
                    <IconEmail />
                  </span>
                  <span className="transition-transform duration-300 group-hover:translate-x-1">
                    {ms365Data.cta}
                  </span>
                </div>
                
                {/* Hover shine effect */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity 
                              duration-300 bg-gradient-to-r from-transparent via-white to-transparent 
                              transform translate-x-[-100%] group-hover:translate-x-[100%] 
                              transition-transform duration-700" />
              </button>
            </div>
            
            {/* Feature cards with entry animations */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {features.map((feature, i) => (
                <div 
                  key={`mobile-feature-${i}`} 
                  className="rounded-xl bg-gray-800/95 border border-blue-600/30 hover:border-blue-400 p-5 
                            flex items-start transition-all duration-300 cursor-pointer hover:shadow-md
                            transform hover:translate-y-[-2px]"
                  style={{
                    animation: `fade-in-up 0.6s ease-out forwards ${i * 0.15}s`,
                    opacity: 0,
                  }}
                >
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0 transition-transform transform hover:scale-110 text-white"
                       style={{ backgroundColor: feature.iconBgColor }}>
                    {feature.icon}
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-white text-sm mb-1">
                      {feature.title}
                    </h4>
                    
                    <div className="text-blue-400 text-xs font-medium mb-1">
                      {feature.product}
                    </div>
                    
                    <div className="text-gray-400 text-xs">
                      {feature.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
    </Container>
  );
};

export default MS365AlternativeUniverse;